import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEUR, formatApiError, API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Download, Mail, Plus, Receipt, Ban, RotateCcw, Eye, FileText, Filter, CalendarRange, Calendar as CalIcon
} from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const METHODS = [
  { v: "bonifico", label: "Bonifico bancario" },
  { v: "contanti", label: "Contanti" },
  { v: "pos", label: "POS" },
  { v: "carta", label: "Carta" },
  { v: "paypal", label: "PayPal" },
  { v: "altro", label: "Altro" },
];
const STATUS_UI = {
  da_pagare: { color: "rose", dot: "🔴", label: "DA PAGARE" },
  parzialmente_pagato: { color: "amber", dot: "🟠", label: "PARZIALMENTE PAGATO" },
  pagato: { color: "emerald", dot: "🟢", label: "PAGATO" },
  pagamento_in_eccesso: { color: "sky", dot: "🔵", label: "PAGAMENTO IN ECCESSO" },
  nessun_servizio: { color: "slate", dot: "⚪", label: "NESSUN SERVIZIO" },
};

function StatusPill({ status, size = "sm" }) {
  const s = STATUS_UI[status] || STATUS_UI.nessun_servizio;
  const colorCls = {
    rose: "text-rose-400 bg-rose-500/10 border-rose-500/40",
    amber: "text-amber-300 bg-amber-500/10 border-amber-500/40",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/40",
    sky: "text-sky-400 bg-sky-500/10 border-sky-500/40",
    slate: "text-slate-400 bg-slate-800/40 border-slate-700",
  }[s.color];
  const sz = size === "lg" ? "text-xs px-3 py-1.5" : "text-[10px] px-2 py-1";
  return <span data-testid={`status-${status}`} className={`inline-flex items-center gap-1 uppercase tracking-widest font-mono-num font-bold border rounded-full ${colorCls} ${sz}`}>{s.dot} {s.label}</span>;
}

function Kpi({ label, value, hint, color = "amber", testid }) {
  const c = { amber: "text-amber-400", emerald: "text-emerald-400", rose: "text-rose-400", sky: "text-sky-400", white: "text-white" }[color];
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</div>
      <div className={`mt-2 font-mono-num text-3xl font-bold ${c}`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-500 font-mono-num mt-1">{hint}</div>}
    </div>
  );
}

function PaymentModal({ open, onClose, customer, summary, onSaved }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bonifico");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [extRef, setExtRef] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      const def = Math.max(0, Number(summary?.balance || 0));
      setAmount(def > 0 ? def.toFixed(2) : "");
      setMethod("bonifico"); setDate(new Date().toISOString().slice(0, 10)); setNotes(""); setExtRef("");
    }
  }, [open, summary]);

  const save = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Inserisci un importo valido"); return; }
    setBusy(true);
    try {
      await api.post("/payments", {
        type: "customer",
        customer_id: customer.id,
        amount: amt,
        method,
        date,
        notes: notes || null,
        external_reference: extRef || null,
        summary_period: summary?.ym || null,
      });
      toast.success("Pagamento registrato");
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white">Registra pagamento</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono-num">Cliente / Periodo</div>
            <div className="text-white font-display font-extrabold text-base">{customer?.name}</div>
            <div className="text-[11px] text-slate-400 font-mono-num mt-0.5">{summary?.label || "—"}</div>
            <div className="grid grid-cols-3 gap-3 mt-2 text-xs">
              <div><div className="text-slate-500">Totale dovuto</div><div className="font-mono-num text-white">{fmtEUR(summary?.total_services || 0)}</div></div>
              <div><div className="text-slate-500">Già pagato</div><div className="font-mono-num text-emerald-400">{fmtEUR(summary?.total_paid || 0)}</div></div>
              <div><div className="text-slate-500">Saldo</div><div className="font-mono-num text-amber-400 font-bold">{fmtEUR(summary?.balance || 0)}</div></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Importo *</Label>
              <Input data-testid="pay-amount" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="bg-slate-900 border-slate-800 font-mono-num" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Data *</Label>
              <Input data-testid="pay-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-slate-900 border-slate-800" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Metodo *</Label>
            <select data-testid="pay-method" value={method} onChange={e => setMethod(e.target.value)} className="w-full h-9 rounded-md bg-slate-900 border border-slate-800 text-sm px-3">
              {METHODS.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Riferimento esterno (opzionale)</Label>
            <Input data-testid="pay-extref" value={extRef} onChange={e => setExtRef(e.target.value)} placeholder="N° ricevuta / CRO bonifico" className="bg-slate-900 border-slate-800" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Note</Label>
            <Input data-testid="pay-notes" value={notes} onChange={e => setNotes(e.target.value)} className="bg-slate-900 border-slate-800" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="border-slate-800 bg-transparent text-slate-300">Annulla</Button>
            <Button data-testid="pay-save" disabled={busy} onClick={save} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">Registra pagamento</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryDetailModal({ open, onClose, customer, summary, driversMap, vehiclesMap, onPay, onChanged }) {
  if (!summary) return null;
  const voidPayment = async (p) => {
    const reason = prompt("Motivo dello storno (opzionale):") || "";
    try {
      await api.post(`/payments/${p.id}/void`, { reason });
      toast.success("Pagamento stornato");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reinstate = async (p) => {
    try {
      await api.post(`/payments/${p.id}/reinstate`);
      toast.success("Pagamento ripristinato");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const pdfUrl = `${API_BASE}/customers/${customer.id}/summary/pdf?year=${summary.year}&month=${summary.month}`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-5xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white flex items-center justify-between">
            <span>Riepilogo · <span className="text-amber-400">{summary.label}</span></span>
            <StatusPill status={summary.status} size="lg"/>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Servizi" value={summary.bookings_count} color="white"/>
            <Kpi label="Totale servizi" value={fmtEUR(summary.total_services)} color="amber"/>
            <Kpi label="Pagato" value={fmtEUR(summary.total_paid)} color="emerald"/>
            <Kpi label={summary.balance >= 0 ? "Da incassare" : "Eccedenza"} value={fmtEUR(Math.abs(summary.balance))} color={summary.balance > 0 ? "rose" : "sky"}/>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid="detail-pay" onClick={onPay} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"><Plus size={16}/> Registra pagamento</Button>
            <a data-testid="detail-pdf" href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-mono-num font-bold px-3 py-2 rounded border border-sky-500/40 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20"><FileText size={14}/> Anteprima PDF</a>
            <a href={pdfUrl} download className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-mono-num font-bold px-3 py-2 rounded border border-slate-700 bg-slate-800/40 text-slate-300 hover:bg-slate-800"><Download size={14}/> Scarica PDF</a>
          </div>

          <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-800/80 text-xs uppercase tracking-widest text-slate-500 font-mono-num">Servizi del periodo</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
                  <tr>
                    <th className="text-left p-3">Data</th><th>Ora</th><th>Tipologia</th>
                    <th>Partenza → Destinazione</th><th>Pax</th><th>Veicolo</th><th>Autista</th>
                    <th className="text-right pr-3">Importo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {summary.bookings.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-500">Nessun servizio</td></tr>}
                  {summary.bookings.map(b => (
                    <tr key={b.id} className="hover:bg-slate-900/40" data-testid={`booking-row-${b.id}`}>
                      <td className="p-3 text-slate-300 font-mono-num">{b.date}</td>
                      <td className="text-slate-400 font-mono-num text-xs">{b.time}</td>
                      <td className="text-slate-300">{b.service_type || "-"}</td>
                      <td className="text-slate-400 text-xs">{b.pickup || "-"} → {b.destination || "-"}</td>
                      <td className="text-slate-400 text-xs font-mono-num">{b.passengers || 1}</td>
                      <td className="text-slate-400 text-xs">{vehiclesMap[b.vehicle_id] || "-"}</td>
                      <td className="text-slate-400 text-xs">{driversMap[b.driver_id] || "-"}</td>
                      <td className="text-right pr-3 text-amber-400 font-mono-num font-bold">{fmtEUR(b.price || 0)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-amber-500/40">
                    <td colSpan={7} className="p-3 text-right text-xs uppercase tracking-widest text-slate-400 font-mono-num font-bold">TOTALE SERVIZI</td>
                    <td className="text-right pr-3 py-3 text-amber-400 font-mono-num font-extrabold text-base">{fmtEUR(summary.total_services)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-800/80 text-xs uppercase tracking-widest text-slate-500 font-mono-num">Storico pagamenti</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
                  <tr>
                    <th className="text-left p-3">Data</th><th>Metodo</th><th>Riferimento</th><th>Note</th>
                    <th className="text-right">Importo</th><th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {summary.payments.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Nessun pagamento</td></tr>}
                  {summary.payments.map(p => (
                    <tr key={p.id} className={`hover:bg-slate-900/40 ${p.voided ? "opacity-50" : ""}`} data-testid={`payment-row-${p.id}`}>
                      <td className="p-3 text-slate-300 font-mono-num">{p.date}</td>
                      <td className="text-slate-300">{(METHODS.find(m => m.v === (p.method || "").toLowerCase()) || { label: p.method })?.label || "-"}</td>
                      <td className="text-slate-400 text-xs">{p.external_reference || p.transaction_id || "-"}</td>
                      <td className="text-slate-400 text-xs">
                        {p.voided ? <span className="text-rose-400 font-bold">[STORNATO{p.void_reason ? `: ${p.void_reason}` : ""}]</span> : (p.notes || "-")}
                      </td>
                      <td className={`text-right font-mono-num font-bold ${p.voided ? "text-slate-500 line-through" : "text-emerald-400"}`}>{fmtEUR(p.amount || 0)}</td>
                      <td className="pr-3">
                        {p.voided ? (
                          <button onClick={() => reinstate(p)} title="Ripristina" className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-emerald-400"><RotateCcw size={14}/></button>
                        ) : (
                          <button data-testid={`void-${p.id}`} onClick={() => voidPayment(p)} title="Storna" className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-rose-400"><Ban size={14}/></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-emerald-500/40">
                    <td colSpan={4} className="p-3 text-right text-xs uppercase tracking-widest text-slate-400 font-mono-num font-bold">TOTALE PAGATO (non stornati)</td>
                    <td className="text-right pr-3 py-3 text-emerald-400 font-mono-num font-extrabold text-base">{fmtEUR(summary.total_paid)}</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td colSpan={4} className="p-3 text-right text-xs uppercase tracking-widest text-slate-400 font-mono-num font-bold">SALDO</td>
                    <td className={`text-right pr-3 py-3 font-mono-num font-extrabold text-base ${summary.balance > 0 ? "text-amber-400" : summary.balance < 0 ? "text-sky-400" : "text-emerald-400"}`}>{fmtEUR(summary.balance)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SendEmailModal({ open, onClose, customer, summary }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && summary) {
      setTo(customer?.email || "");
      setSubject(`Riepilogo servizi — ${summary.label}`);
      setMessage("");
    }
  }, [open, customer, summary]);
  const send = async () => {
    if (!to) { toast.error("Email destinatario mancante"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/customers/${customer.id}/summary/send`, {
        year: summary.year, month: summary.month, to, subject, message
      });
      toast.success("Email inviata al cliente");
      if (r.data?.note) toast.info(r.data.note);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-xl text-white">Invia riepilogo al cliente</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Destinatario *</Label>
            <Input data-testid="email-to" type="email" value={to} onChange={e => setTo(e.target.value)} className="bg-slate-900 border-slate-800" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Oggetto</Label>
            <Input data-testid="email-subject" value={subject} onChange={e => setSubject(e.target.value)} className="bg-slate-900 border-slate-800" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Messaggio aggiuntivo</Label>
            <textarea data-testid="email-message" rows={4} value={message} onChange={e => setMessage(e.target.value)} className="w-full rounded-md bg-slate-900 border border-slate-800 text-sm px-3 py-2"/>
          </div>
          <div className="text-[10px] text-amber-400 bg-amber-500/5 border border-amber-500/20 rounded p-2">
            ⚠️ Il canale email corrente non supporta allegati PDF. L'email conterrà i totali del riepilogo in testo. Scarica il PDF e invialo manualmente se serve.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="border-slate-800 bg-transparent text-slate-300">Annulla</Button>
            <Button data-testid="email-send" disabled={busy} onClick={send} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"><Mail size={16}/> Invia</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function RiepiloghiTab({ customer, driversMap, vehiclesMap }) {
  const [scope, setScope] = useState("all"); // all | month | range
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState({ months: [], totals: { services_count: 0, total_services: 0, total_paid: 0, balance: 0, status: "nessun_servizio" } });
  const [situation, setSituation] = useState({ services_count: 0, total_services: 0, total_paid: 0, to_collect: 0, balance: 0 });
  const [loading, setLoading] = useState(false);
  const [detailMonth, setDetailMonth] = useState(null);
  const [payModal, setPayModal] = useState(null); // summary object
  const [emailModal, setEmailModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { scope };
      if (scope === "month") { params.year = year; params.month = month; }
      else if (scope === "range") { params.date_from = dateFrom; params.date_to = dateTo; }
      const [sumRes, sitRes] = await Promise.all([
        api.get(`/customers/${customer.id}/summaries`, { params }),
        api.get(`/customers/${customer.id}/situation`),
      ]);
      setData(sumRes.data);
      setSituation(sitRes.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customer?.id]);

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y - 2, y - 1, y, y + 1];
    // eslint-disable-next-line
  }, []);

  const openDetail = (ymKey) => {
    const m = data.months.find(x => x.ym === ymKey);
    setDetailMonth(m);
  };
  const refreshDetailMonth = async () => {
    await load();
    if (detailMonth) {
      // after load, re-sync
      setTimeout(() => {
        setDetailMonth(prev => prev ? (data.months.find(x => x.ym === prev.ym) || prev) : null);
      }, 50);
    }
  };
  // keep detailMonth in sync after load
  useEffect(() => {
    if (detailMonth) {
      const fresh = data.months.find(x => x.ym === detailMonth.ym);
      if (fresh) setDetailMonth(fresh);
    }
    // eslint-disable-next-line
  }, [data]);

  return (
    <div className="space-y-6" data-testid="riepiloghi-tab">
      {/* KPI situazione contabile globale */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="kpi-services-count" label="Servizi totali" value={situation.services_count} color="white" hint="Tutto il periodo"/>
        <Kpi testid="kpi-total-services" label="Totale servizi" value={fmtEUR(situation.total_services)} color="amber"/>
        <Kpi testid="kpi-total-paid" label="Totale pagato" value={fmtEUR(situation.total_paid)} color="emerald"/>
        <Kpi testid="kpi-to-collect" label="Totale da incassare" value={fmtEUR(situation.to_collect)} color="rose"/>
      </div>

      {/* Filtro periodo */}
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-amber-400"/>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-num">Filtro periodo</div>
        </div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <button data-testid="scope-all" onClick={() => setScope("all")} className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${scope === "all" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400 hover:text-slate-200"}`}>Tutti i periodi</button>
          <button data-testid="scope-month" onClick={() => setScope("month")} className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${scope === "month" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400 hover:text-slate-200"}`}><CalIcon size={12} className="inline"/> Mese / Anno</button>
          <button data-testid="scope-range" onClick={() => setScope("range")} className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest font-mono-num font-bold ${scope === "range" ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-slate-700 bg-slate-800/40 text-slate-400 hover:text-slate-200"}`}><CalendarRange size={12} className="inline"/> Personalizzato</button>
        </div>
        {scope === "month" && (
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Mese</Label>
              <select data-testid="filter-month" value={month} onChange={e => setMonth(Number(e.target.value))} className="h-9 rounded-md bg-slate-900 border border-slate-800 text-sm px-3">{MONTHS.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}</select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Anno</Label>
              <select data-testid="filter-year" value={year} onChange={e => setYear(Number(e.target.value))} className="h-9 rounded-md bg-slate-900 border border-slate-800 text-sm px-3">{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
            </div>
            <Button data-testid="apply-filter" onClick={load} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-9">Applica filtro</Button>
          </div>
        )}
        {scope === "range" && (
          <div className="flex items-end gap-2 flex-wrap">
            <div><Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Data iniziale</Label><Input data-testid="filter-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-slate-900 border-slate-800 h-9"/></div>
            <div><Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Data finale</Label><Input data-testid="filter-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-slate-900 border-slate-800 h-9"/></div>
            <Button data-testid="apply-filter-range" onClick={load} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-9">Applica filtro</Button>
          </div>
        )}
      </div>

      {/* Lista riepiloghi mensili */}
      {loading ? (
        <div className="text-center text-slate-500 py-12">Caricamento...</div>
      ) : data.months.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-800/80 bg-slate-900/20 p-10 text-center">
          <Receipt className="mx-auto text-slate-700 mb-3" size={40}/>
          <div className="text-slate-400 font-mono-num text-sm">Nessun servizio nel periodo selezionato</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="summaries-grid">
          {data.months.map(m => (
            <div key={m.ym} data-testid={`summary-card-${m.ym}`} className="rounded-2xl border border-slate-800/80 bg-gradient-to-br from-slate-900/80 to-slate-950/80 p-5 space-y-3 hover:border-amber-500/40 transition">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-xl font-extrabold text-white uppercase tracking-wide">{m.label}</div>
                  <div className="text-[11px] text-slate-500 font-mono-num mt-0.5">{m.bookings_count} {m.bookings_count === 1 ? "servizio" : "servizi"}</div>
                </div>
                <StatusPill status={m.status}/>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2">
                <div><div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num">Totale</div><div className="font-mono-num text-amber-400 font-bold text-sm">{fmtEUR(m.total_services)}</div></div>
                <div><div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num">Pagato</div><div className="font-mono-num text-emerald-400 font-bold text-sm">{fmtEUR(m.total_paid)}</div></div>
                <div><div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num">{m.balance >= 0 ? "Da incassare" : "Eccedenza"}</div><div className={`font-mono-num font-bold text-sm ${m.balance > 0 ? "text-rose-400" : m.balance < 0 ? "text-sky-400" : "text-emerald-400"}`}>{fmtEUR(Math.abs(m.balance))}</div></div>
              </div>
              <div className="flex gap-1 pt-2 flex-wrap">
                <button data-testid={`view-${m.ym}`} onClick={() => openDetail(m.ym)} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-slate-700 bg-slate-800/40 text-slate-300 hover:text-amber-400 text-[10px] uppercase tracking-widest font-mono-num font-bold"><Eye size={12}/> Visualizza</button>
                <a data-testid={`pdf-${m.ym}`} href={`${API_BASE}/customers/${customer.id}/summary/pdf?year=${m.year}&month=${m.month}`} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-sky-500/40 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20 text-[10px] uppercase tracking-widest font-mono-num font-bold"><FileText size={12}/> PDF</a>
                <button data-testid={`email-${m.ym}`} onClick={() => setEmailModal(m)} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-slate-700 bg-slate-800/40 text-slate-300 hover:text-amber-400 text-[10px] uppercase tracking-widest font-mono-num font-bold"><Mail size={12}/> Invia</button>
                <button data-testid={`pay-${m.ym}`} onClick={() => setPayModal(m)} className="w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 text-[10px] uppercase tracking-widest font-mono-num font-bold"><Plus size={12}/> Registra pagamento</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Totali del periodo filtrato */}
      {data.months.length > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono-num mb-3">RIEPILOGO DEL PERIODO FILTRATO</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Servizi</div><div className="font-mono-num text-2xl font-bold text-white">{data.totals.services_count}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Totale</div><div className="font-mono-num text-2xl font-bold text-amber-400">{fmtEUR(data.totals.total_services)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Pagato</div><div className="font-mono-num text-2xl font-bold text-emerald-400">{fmtEUR(data.totals.total_paid)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Saldo</div><div className={`font-mono-num text-2xl font-bold ${data.totals.balance > 0 ? "text-rose-400" : data.totals.balance < 0 ? "text-sky-400" : "text-emerald-400"}`}>{fmtEUR(data.totals.balance)}</div></div>
          </div>
          <div className="mt-3"><StatusPill status={data.totals.status} size="lg"/></div>
        </div>
      )}

      <SummaryDetailModal
        open={!!detailMonth}
        onClose={() => setDetailMonth(null)}
        customer={customer}
        summary={detailMonth}
        driversMap={driversMap || {}}
        vehiclesMap={vehiclesMap || {}}
        onPay={() => { setPayModal(detailMonth); }}
        onChanged={refreshDetailMonth}
      />
      <PaymentModal open={!!payModal} onClose={() => setPayModal(null)} customer={customer} summary={payModal} onSaved={load}/>
      <SendEmailModal open={!!emailModal} onClose={() => setEmailModal(null)} customer={customer} summary={emailModal}/>
    </div>
  );
}
